// routes/components/PlayerPublicRoute.tsx
/**
 * PlayerPublicRoute - Redirects authenticated users away from the
 * player registration / public landing.
 *
 * - Authenticated player → /player.
 * - Authenticated staff with no remembered preference → /welcome
 *   picker. (They might still want to sign their kid in via the
 *   player register page, but that's a separate flow accessed from
 *   the welcome / nav.)
 * - Authenticated staff with a remembered preference → that target.
 * - Unauthenticated → render the registration / landing children.
 */

import { ReactElement } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { PageLoading } from '../../components/common/Loading';
import { ROUTES } from '../../config/routes';
import { readWelcomePreference } from '../../features/auth/pages/WelcomePage';
import type { PlayerPublicRouteProps } from '../types';

export function PlayerPublicRoute({
  children,
}: PlayerPublicRouteProps): ReactElement {
  const { user, loading, isPlayer } = useAuth();

  if (loading) {
    return <PageLoading />;
  }

  if (user) {
    if (isPlayer()) {
      return <Navigate to={ROUTES.PLAYER_DASHBOARD} replace />;
    }
    const remembered = readWelcomePreference();
    if (remembered === 'staff') {
      return <Navigate to={ROUTES.LANDING} replace />;
    }
    if (remembered === 'player') {
      return <Navigate to={ROUTES.PLAYER_DASHBOARD} replace />;
    }
    return <Navigate to={ROUTES.WELCOME} replace />;
  }

  return <>{children}</>;
}

export default PlayerPublicRoute;
